"use client";
import { useEffect, useId, useMemo, useState } from "react";
import {
  imageThumbnailUrl,
  normalizeImageUrl,
  type ImageThumbnailSize,
} from "@/lib/imageUrls";
import noItemIcon from "@/assets/NoItem.svg";

export type Tone = "neutral" | "ochre" | "rose" | "amber" | "tomato" | "olive" | "honey";

const TONES: Record<Tone, [string, string]> = {
  neutral: ["#f2f3f1", "#d8ddd7"],
  ochre: ["#fde9c7", "#e9b977"],
  rose: ["#fbe2dc", "#e89c8b"],
  amber: ["#fbe6c4", "#e3a25a"],
  tomato: ["#f7d8cf", "#d8634d"],
  olive: ["#e2e6c7", "#9aa66b"],
  honey: ["#fce9bf", "#d6a652"],
};

type FoodImgProps = {
  tone?: Tone;
  label?: string;
  src?: string | null;
  alt?: string;
  square?: boolean;
  rounded?: number;
  className?: string;
  style?: React.CSSProperties;
  thumbnailSize?: ImageThumbnailSize;
};

export function FoodImg({
  tone = "neutral",
  label,
  src,
  alt,
  square,
  rounded = 14,
  className = "",
  style = {},
  thumbnailSize = "400x400",
}: FoodImgProps) {
  const [failed, setFailed] = useState(false);
  const [useOriginal, setUseOriginal] = useState(false);
  const [toneA, toneB] = TONES[tone] || TONES.neutral;
  const idBase = useId().replace(/[:]/g, "");
  const id = `g${idBase}`;
  const originalSrc = useMemo(() => normalizeImageUrl(src), [src]);
  const thumbnailSrc = useMemo(
    () => imageThumbnailUrl(src, thumbnailSize),
    [src, thumbnailSize],
  );
  const imgSrc = useOriginal ? originalSrc : thumbnailSrc;
  const showImage = !!imgSrc && !failed;
  const [a, b] = showImage ? [toneA, toneB] : TONES.neutral;

  useEffect(() => {
    setFailed(false);
    setUseOriginal(false);
  }, [src, thumbnailSize]);

  return (
    <div
      className={className}
      style={{
        width: "100%",
        aspectRatio: square ? "1 / 1" : "16 / 10",
        borderRadius: rounded,
        overflow: "hidden",
        position: "relative",
        background: a,
        ...style,
      }}
    >
      {showImage ? (
        <img
          src={imgSrc}
          alt={alt ?? label ?? ""}
          onError={() => {
            if (!useOriginal && originalSrc && thumbnailSrc && thumbnailSrc !== originalSrc) {
              setUseOriginal(true);
              return;
            }
            setFailed(true);
          }}
          style={{
            display: "block",
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#FFF4E6",
          }}
        >
          <img
            src={noItemIcon}
            alt={alt ?? label ?? ""}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
            }}
          />
        </div>
      )}
    </div>
  );
}
